/**
 * Scroll-triggered animations using IntersectionObserver.
 *
 * Usage:
 *   data-animate="fade-up"        — fade up on scroll
 *   data-animate="fade-in"        — fade in on scroll
 *   data-animate="fade-right"     — fade in from left
 *   data-animate="fade-left"      — fade in from right
 *
 * Staggered children:
 *   data-animate-stagger          — on a parent, staggers [data-animate-child] items
 *   data-animate-child            — on each child element
 *
 * Options (HTML attributes):
 *   data-animate-delay="200"      — delay in ms before animation starts
 *   data-animate-offset="100"     — rootMargin bottom offset (default: 80px)
 */

const DEFAULTS = {
  offset: 80,
  staggerInterval: 120,
};

const animateElement = (el: HTMLElement): void => {
  const delay = parseInt(el.getAttribute('data-animate-delay') || '0', 10);

  if (delay > 0) {
    setTimeout(() => el.classList.add('is-visible'), delay);
  } else {
    el.classList.add('is-visible');
  }
};

const initAnimations = (): void => {
  // Respect reduced motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // Make everything visible immediately
    document
      .querySelectorAll<HTMLElement>('[data-animate], [data-animate-child]')
      .forEach((el) => el.classList.add('is-visible'));
    return;
  }

  // Individual element animations
  const animatedElements = document.querySelectorAll<HTMLElement>('[data-animate]');

  animatedElements.forEach((el) => {
    const offset = parseInt(el.getAttribute('data-animate-offset') || String(DEFAULTS.offset), 10);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateElement(entry.target as HTMLElement);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: `0px 0px -${offset}px 0px`,
        threshold: 0,
      }
    );

    observer.observe(el);
  });

  // Staggered parent animations
  const staggerParents = document.querySelectorAll<HTMLElement>('[data-animate-stagger]');

  staggerParents.forEach((parent) => {
    const children = parent.querySelectorAll<HTMLElement>('[data-animate-child]');
    const offset = parseInt(parent.getAttribute('data-animate-offset') || String(DEFAULTS.offset), 10);
    const interval = parseInt(parent.getAttribute('data-animate-interval') || String(DEFAULTS.staggerInterval), 10);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            children.forEach((child, index) => {
              setTimeout(() => {
                child.classList.add('is-visible');
              }, index * interval);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: `0px 0px -${offset}px 0px`,
        threshold: 0,
      }
    );

    observer.observe(parent);
  });
};

export default initAnimations;
